import { motion } from "framer-motion";
import { Cpu, Shield, Wifi, Gauge } from "lucide-react";
import { useLanguage } from "@/contexts/language-context";

export function TechShowcase() {
  const { t } = useLanguage();

  return (
    <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-500 via-transparent to-transparent" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-block mb-4 px-6 py-2 bg-blue-500/20 border border-blue-500/30 rounded-full">
            <span className="text-blue-400 font-semibold">{t("tech.badge")}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t("tech.title")}
            <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              {t("tech.titleHighlight")}
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t("tech.subtitle")}
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto">
          {/* Technology Highlights Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {[
              {
                icon: Cpu,
                title: t("tech.driveByWire.title"),
                description: t("tech.driveByWire.description"),
                image: "/img/底盘悬挂系统.png",
                flip: false,
              },
              {
                icon: Shield,
                title: t("tech.bodyStructure.title"),
                description: t("tech.bodyStructure.description"),
                image: "/img/车身框架结构.png",
                flip: false,
              },
              {
                icon: Wifi,
                title: t("tech.connected.title"),
                description: t("tech.connected.description"),
                image: "/img/数字仪表盘.png",
                flip: false,
              },
              {
                icon: Gauge,
                title: t("tech.driving.title"),
                description: t("tech.driving.description"),
                image: "/img/内饰-仪表台正面.webp",
                flip: true,
              },
            ].map((tech, index) => {
              const Icon = tech.icon;
              return (
                <motion.div
                  key={tech.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="group relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl" />
                  <div className="relative bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-3xl overflow-hidden hover:border-slate-600 transition-all duration-300">
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img src={tech.image}
                        alt={tech.title}
                        className="object-cover"
                        sizes="(max-width: 768px) 100vw, 50vw"
                        style={tech.flip ? { transform: "scaleX(-1)", width: "100%", height: "100%", objectFit: "cover" } : {width: "100%", height: "100%", objectFit: "cover"}} />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
                    </div>

                    {/* Content */}
                    <div className="p-8">
                      <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl mb-4">
                        <Icon className="w-7 h-7" />
                      </div>
                      <h3 className="text-2xl font-bold mb-3">{tech.title}</h3>
                      <p className="text-gray-400 leading-relaxed">
                        {tech.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

        
        </div>
      </div>
    </section>
  );
}

